import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { Address } from '@/types/order'

const addressSchema = z.object({
  firstName: z.string().min(1, 'validation.required'),
  lastName: z.string().min(1, 'validation.required'),
  street: z.string().min(1, 'validation.required'),
  houseNumber: z.string().min(1, 'validation.required'),
  postalCode: z.string().regex(/^\d{5}$/, 'validation.postalCode'),
  city: z.string().min(1, 'validation.required'),
  countryCode: z.string().default('DE'),
})

export type AddressFormData = z.infer<typeof addressSchema>

interface AddressFormProps {
  defaultValues?: Partial<Address>
  onSubmit: (data: AddressFormData) => void
  title?: string
}

const AddressForm = ({ defaultValues, onSubmit, title }: AddressFormProps) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      street: defaultValues?.street || '',
      houseNumber: defaultValues?.houseNumber || '',
      postalCode: defaultValues?.postalCode || '',
      city: defaultValues?.city || '',
      countryCode: defaultValues?.countryCode || 'DE',
    },
  })

  const formData = watch()

  // Auto-submit when form is valid and all fields are filled
  useEffect(() => {
    if (isValid && formData.firstName && formData.lastName && formData.street && formData.houseNumber && formData.postalCode && formData.city) {
      onSubmit(formData)
    }
  }, [formData, isValid, onSubmit])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {title && (
        <h3 className="text-xl font-bold text-gray-100 mb-4">{title}</h3>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('checkout.firstName')} *
          </label>
          <input
            {...register('firstName')}
            className="w-full px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('checkout.firstNamePlaceholder')}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-400">{t(errors.firstName.message || 'validation.required')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('checkout.lastName')} *
          </label>
          <input
            {...register('lastName')}
            className="w-full px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('checkout.lastNamePlaceholder')}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-400">{t(errors.lastName.message || 'validation.required')}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {t('checkout.street')} *
        </label>
        <div className="grid grid-cols-3 gap-2">
          <input
            {...register('street')}
            className="col-span-2 px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('checkout.streetPlaceholder')}
          />
          <input
            {...register('houseNumber')}
            className="px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('checkout.houseNumberPlaceholder')}
          />
        </div>
        {(errors.street || errors.houseNumber) && (
          <p className="mt-1 text-sm text-red-400">
            {t(errors.street?.message || errors.houseNumber?.message || 'validation.required')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('checkout.postalCode')} *
          </label>
          <input
            {...register('postalCode')}
            className="w-full px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="12345"
            maxLength={5}
          />
          {errors.postalCode && (
            <p className="mt-1 text-sm text-red-400">{t(errors.postalCode.message || 'validation.postalCode')}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('checkout.city')} *
          </label>
          <input
            {...register('city')}
            className="w-full px-4 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('checkout.cityPlaceholder')}
          />
          {errors.city && (
            <p className="mt-1 text-sm text-red-400">{t(errors.city.message || 'validation.required')}</p>
          )}
        </div>
      </div>

      <input type="hidden" {...register('countryCode')} value="DE" />
    </form>
  )
}

export default AddressForm
